import { Component, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { MaterialApi } from "../../../Material/Api/MaterialApi";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";
import { StockEffects } from "../../State/StockEffects";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { StockAdjustmentType } from "../../Dto/CreateStockAdjustmentRequest";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import { MaterialAutocompleteOption, toMaterialAutocompleteOption } from "../../../../Common/Utility/UtilityMaterialSequence";

@Component({
  selector: "app-stock-initial-adjustment-create",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AutocompleteComponent,
    RichTextEditorComponent,
    QtyInputSelectZeroDirective,
  ],
  templateUrl: "./StockInitialAdjustmentCreatePage.html",
  styleUrl: "./StockInitialAdjustmentCreatePage.scss",
})
export class StockAdjustmentCreatePage {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly stockEffects = inject(StockEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly materialApi = inject(MaterialApi);
  private readonly warehouseApi = inject(WarehouseApi);

  isSaving = false;
  materialOptions: MaterialAutocompleteOption[] = [];
  warehouseOptions: AutocompleteOption[] = [];
  readonly adjustmentTypeOptions: StockAdjustmentType[] = ["Initial", "Increase", "Decrease"];

  form = this.fb.group({
    materialId: ["", [Validators.required]],
    warehouseId: ["", [Validators.required]],
    adjustmentType: ["Initial" as StockAdjustmentType, [Validators.required]],
    qty: [1, [Validators.required, Validators.min(1)]],
    note: [""],
  });

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    this.isSaving = true;
    try {
      await this.stockEffects.createAdjustment({
        materialId: (this.form.value.materialId ?? "").trim(),
        warehouseId: (this.form.value.warehouseId ?? "").trim(),
        adjustmentType: (this.form.value.adjustmentType ?? "Initial") as StockAdjustmentType,
        qty: Number(this.form.value.qty ?? 0),
        note: (this.form.value.note ?? "").trim(),
      });

      this.toastEffects.show("Stock adjustment created.", "success");
      await this.router.navigate(["/stocks"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create stock adjustment."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/stocks"]).catch(() => undefined);
  }

  async searchMaterials(term: string): Promise<void> {
    const response = await this.materialApi.searchMaterials({ search: term, page: 1, limit: 50 });
    this.materialOptions = response.data.map((item) => toMaterialAutocompleteOption(item));
  }

  async searchWarehouses(term: string): Promise<void> {
    const response = await this.warehouseApi.searchWarehouses({ search: term, page: 1, limit: 50 });
    this.warehouseOptions = response.data.map((item) => ({ value: item.id, label: item.name }));
  }

  onMaterialChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.form.patchValue({ materialId: value.toString() });
  }

  onWarehouseChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.form.patchValue({ warehouseId: value.toString() });
  }
}
